import React from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import router from "./router";
import { ThemeProvider, SkinProvider } from "./contexts";
import "remixicon/fonts/remixicon.css";
import "katex/dist/katex.min.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/roboto-mono/400.css";
import "./styles/main.css";
import "./styles/code-highlight.css";
import "./styles/mobile.css"; // 导入移动端优化CSS
import "./styles/mobile-modals.css"; // 导入移动端弹窗优化CSS
import initializeCodeHighlighter from "./utils/initializeHighlighter";

// 初始化代码高亮器
initializeCodeHighlighter();

// 创建根元素
const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <ThemeProvider>
      <SkinProvider>
        <RouterProvider router={router} />
      </SkinProvider>
    </ThemeProvider>
  );

  // 创建轻量级背景数学符号动画（仅少量数学符号，零定时器开销）
  createMathSymbols();
} else {
  console.error("Root element not found!");
}

function createMathSymbols() {
  // 尊重系统减弱动态效果偏好
  if (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  const mathAnimationContainer = document.createElement("div");
  mathAnimationContainer.className = "math-animation";
  mathAnimationContainer.setAttribute("aria-hidden", "true");

  // 仅保留纯粹的经典数学符号，移除所有月饼、小花、玉兔、灯笼等节日图案
  const symbols = [
    "∫",
    "∑",
    "∏",
    "π",
    "√",
    "∞",
    "θ",
    "φ",
    "λ",
    "Δ",
    "∇",
    "∂",
    "∀",
    "∃",
    "∈",
    "ℝ",
  ];

  const colors = [
    "#4285f4", // 蓝色
    "#34a853", // 绿色
    "#fbbc05", // 黄色
    "#9b51e0", // 紫色
    "#00c6ff", // 青色
  ];

  // 仅一次性创建少量（10个）数学符号，完全交由 CSS infinite 动画循环驱动
  // 避免 setInterval / setTimeout 频繁增删 DOM 节点及底部堆积问题
  const SYMBOL_COUNT = 10;
  const fragment = document.createDocumentFragment();

  for (let i = 0; i < SYMBOL_COUNT; i++) {
    const symbol = document.createElement("div");
    symbol.className = "math-symbol";
    symbol.textContent = symbols[i % symbols.length];

    const size = (1.2 + (i % 3) * 0.45).toFixed(2); // 1.2rem - 2.1rem
    // 均匀分布水平位置并加入微小随机偏移，避免重叠
    const slotWidth = 100 / SYMBOL_COUNT;
    const startLeftPosition = (
      i * slotWidth +
      slotWidth * 0.2 +
      Math.random() * (slotWidth * 0.6)
    ).toFixed(1);
    const opacity = (0.28 + (i % 3) * 0.08).toFixed(2); // 0.28 - 0.44
    const duration = (22 + (i % 5) * 4).toFixed(1); // 22 - 38秒
    // 使用负延迟让符号在首屏即均匀分布在各高度轨迹上，绝不在页面底部堆积
    const negativeDelay = -((i / SYMBOL_COUNT) * Number(duration)).toFixed(2);
    const color = colors[i % colors.length];

    symbol.style.fontSize = `${size}rem`;
    symbol.style.left = `${startLeftPosition}%`;
    symbol.style.color = color;
    symbol.style.setProperty("--symbol-opacity", opacity);
    symbol.style.animationDuration = `${duration}s`;
    symbol.style.animationDelay = `${negativeDelay}s`;

    fragment.appendChild(symbol);
  }

  mathAnimationContainer.appendChild(fragment);
  document.body.appendChild(mathAnimationContainer);
}

